import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Diaporama } from "@/components/Diaporama";
import { Producteur } from "@/components/Producteur";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Lock,
  FileText,
  ShieldCheck,
  CalendarClock,
  Receipt,
  Landmark,
  MonitorSmartphone,
  Sparkles,
  Menu,
  X,
} from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";

const shuffle = (arr) => {  // ordre aléatoire à chaque visite : toutes les compagnies/spectacles tournent
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const HERO_IMG =
  "https://images.squarespace-cdn.com/content/v1/65ea2001529ccd1a677ed52f/da9502a2-8fab-4928-8fba-39a0bb756a60/Le+30+Du+mois.png";

const LOGO =
  "https://images.squarespace-cdn.com/content/v1/65ea2001529ccd1a677ed52f/3543688a-b7e0-4677-901b-3c9d2aa2b063/LogoL30M-bleu-nuit.png";

const CLIENTS = [
  "Cie Amalgam", "Circo De Fantoche", "Glaçons Caféinés", "Compagnie Curupira",
  "Broadway", "Les Marginolz", "Cie Samizdat", "Théâtre du Vertige",
  "Collectif I.O.D.", "20 Degrés dans le Noir", "Cie La Serre", "Compagnie Nö",
  "Compagnie Liubov", "Compagnie Bleu Nuit", "La Nuit d'Après", "Hagard Compagnie",
  "Ensemble Soundinitiative", "Le Quatuor Ariane", "Miréno Théâtre", "Ram Dam",
];

const CHAPTERS = [
  {
    n: "01",
    title: "Notre métier",
    body:
      "Le 30 du mois est spécialisé dans la paye des artistes et des techniciens du spectacle vivant. Nous accompagnons les Entreprises Artistiques et Culturelles (CCN 1285), le Spectacle Vivant Privé (CCN 3090) et les Organismes de Formation (CCN 1516).",
  },
  {
    n: "02",
    title: "Pourquoi externaliser",
    body:
      "Les réglementations et conventions collectives évoluent vite. Externaliser votre paye vous protège des risques d'une paye non conforme, vous recentre sur votre activité artistique et vous fait gagner un temps précieux au quotidien.",
  },
  {
    n: "03",
    title: "Nos engagements",
    body:
      "Respect strict de la réglementation en vigueur, sécurisation de chaque procédure et protection de vos données conformément au RGPD. Un espace dématérialisé pour transmettre et recevoir tous vos documents en toute confidentialité.",
  },
  {
    n: "04",
    title: "Nos tarifs",
    body: (
      <>
        <span className="block">
          Le 30 du mois fonctionne selon 4 axes de facturation : 1. Création de votre structure, depuis la
          rédaction des statuts jusqu'à l'immatriculation aux caisses et l'envoi de la première déclaration
          d'embauche. 2. Ouverture des comptes sociaux et paramétrage de votre structure et de vos premiers
          salariés dans le logiciel de paye. 3. Participation à la licence et au contrat de maintenance annuel
          du logiciel de paye. 4. Gestion de la paye : de 13 € à 4,60 € (la dégressivité s'applique en fonction
          du nombre de payes émises pour un même salarié).
        </span>
        <a
          href={`${API}/public/tarifs`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="tarifs-link"
          className="detail-vit inline-block mt-5 font-mono text-xs uppercase tracking-[0.15em] text-gold underline underline-offset-4 hover:text-white transition-colors"
        >
          {"Détail ici".split("").map((ch, k) => (
            <span key={k} className="ballet-letter" style={{ animationDelay: `${k * 60}ms` }}>
              {ch === " " ? " " : ch}
            </span>
          ))}
        </a>
      </>
    ),
  },
];

const SERVICES = [
  { icon: Receipt, title: "Bulletins de paye", desc: "Édition et envoi des bulletins, du calcul aux éléments variables." },
  { icon: FileText, title: "DPAE & contrats", desc: "Identification du salarié, déclaration préalable à l'embauche, contrats." },
  { icon: Landmark, title: "Déclarations sociales", desc: "Urssaf, Audiens, France Travail, Afdas, Fnas, médecine du travail." },
  { icon: CalendarClock, title: "Congés Spectacles & AEM", desc: "Bordereaux Congés Spectacles, AEM / AER France Travail." },
  { icon: ShieldCheck, title: "DSN & fiscalité", desc: "Déclaration sociale nominative, taxe sur les salaires, prélèvement à la source." },
  { icon: MonitorSmartphone, title: "Espace dématérialisé", desc: "Un espace sécurisé pour transmettre et recevoir tous vos documents." },
];

function RevealLine({ children, delay = 0 }) {
  return (
    <span className="reveal-mask">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

/* La « curiosité » du titre Liens utiles : une lettre se retourne et change de couleur
   de temps en temps ; le mot frémit au survol (voir .curiosite dans index.css). */
function CuriositeWord({ text }) {
  const [flip, setFlip] = useState(-1);
  useEffect(() => {
    let off;
    const id = setInterval(() => {
      setFlip(Math.floor(Math.random() * text.length));
      off = setTimeout(() => setFlip(-1), 750);
    }, 2300);
    return () => { clearInterval(id); clearTimeout(off); };
  }, [text]);
  return (
    <span className="curiosite" data-testid="curiosite-word" title="La curiosité, évidemment, n'en fait qu'à sa tête.">
      {text.split("").map((ch, i) => (
        <span key={i} className={`curiosite-letter${flip === i ? " curiosite-flip" : ""}`} style={{ transitionDelay: `${i * 18}ms` }}>
          {ch === " " ? " " : ch}
        </span>
      ))}
    </span>
  );
}

const fadeUp = {  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState({ name: "", structure: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const [boardEmail, setBoardEmail] = useState("");
  const [boardBusy, setBoardBusy] = useState(false);
  const [boardErr, setBoardErr] = useState("");
  const boardEnter = async (e) => {
    e.preventDefault();
    setBoardBusy(true);
    setBoardErr("");
    try {
      const r = await api.post("/board/enter", { email: boardEmail });
      sessionStorage.setItem("l30m_board", JSON.stringify({ email: boardEmail.toLowerCase().trim(), name: r.data.name, code: r.data.code }));
      navigate("/bourse");
    } catch (err) {
      setBoardErr(formatApiErrorDetail(err.response?.data?.detail) || "Entrée impossible pour le moment.");
    } finally { setBoardBusy(false); }
  };
  const [clients, setClients] = useState(() => shuffle(CLIENTS));
  const [spectacles, setSpectacles] = useState([]);
  const [spectaclesFresh, setSpectaclesFresh] = useState(false);
  const [liens, setLiens] = useState([]);
  const [sites, setSites] = useState([]);
  // Révérence du « Merci » : 2,6 s toutes les ~14 s (voir .merci-vit dans index.css)
  const [merciVit, setMerciVit] = useState(false);
  useEffect(() => {
    let id;
    let off;
    const start = setTimeout(() => {
      setMerciVit(true);
      off = setTimeout(() => setMerciVit(false), 2700);
      id = setInterval(() => {
        setMerciVit(true);
        off = setTimeout(() => setMerciVit(false), 2700);
      }, 14000);
    }, 6000);
    return () => { clearTimeout(start); clearTimeout(off); if (id) clearInterval(id); };
  }, []);

  useEffect(() => {
    fetch(`${API}/public/references`)
      .then((r) => r.json())
      .then((d) => { if (d.names?.length) setClients(shuffle(d.names)); })
      .catch(() => {});
    fetch(`${API}/public/spectacles`)
      .then((r) => r.json())
      .then((d) => {
        setSpectacles(shuffle(d.items || []));
        setSpectaclesFresh(Boolean(d.fresh));
      })
      .catch(() => {});
    fetch(`${API}/public/liens`)
      .then((r) => r.json())
      .then((d) => setLiens(d.items || []))
      .catch(() => {});
    fetch(`${API}/public/sites`)
      .then((r) => r.json())
      .then((d) => setSites(d.items || []))
      .catch(() => {});
    // Compteur de visites : 1 enregistrement par session navigateur
    if (!sessionStorage.getItem("l30m_visit")) {
      sessionStorage.setItem("l30m_visit", "1");
      fetch(`${API}/public/visit`, { method: "POST" }).catch(() => {});
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Votre demande a bien été envoyée. Nous vous répondons sous 24h ouvrées.");
      setForm({ name: "", structure: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Envoi impossible.");
    } finally {
      setSending(false);
    }
  };

  const navLinks = [
    { label: "Métier", href: "#metier" },
    { label: "Services", href: "#services" },
    { label: "Références", href: "#references" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <div className="bg-ink text-[#F3F4F6] font-body overflow-x-hidden">
      {/* NAV */}
      <header
        data-testid="main-nav"
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-2xl bg-ink/70 border-b border-white/10"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <a href="#top" className="logo-souffle font-serif text-xl tracking-tight" data-testid="brand-logo">
            Le 30 <span className="text-gold italic">du mois</span>
          </a>
          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="font-mono text-xs tracking-[0.15em] uppercase text-[#A0B2CE] hover:text-gold transition-colors duration-300"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/connexion"
              data-testid="nav-login-btn"
              className="group inline-flex items-center gap-2 border border-gold/60 text-gold px-5 py-2.5 rounded-full text-xs font-mono uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <Lock className="w-3.5 h-3.5" /> Espace client
            </Link>
          </nav>
          <button
            className="md:hidden text-[#F3F4F6]"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-white/10 bg-ink px-6 py-6 flex flex-col gap-5">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="font-mono text-xs tracking-[0.15em] uppercase text-[#A0B2CE]">
                {l.label}
              </a>
            ))}
            <Link to="/connexion" className="inline-flex items-center gap-2 text-gold font-mono text-xs uppercase tracking-[0.15em]">
              <Lock className="w-3.5 h-3.5" /> Espace client
            </Link>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-end">
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <img src={`${API}/public/hero-visual`} alt="Metteur en scène entouré de ses comédiens" className="w-full h-full object-cover object-top"
            fetchPriority="high"
            onError={(e) => { if (!e.currentTarget.dataset.fbk) { e.currentTarget.dataset.fbk = "1"; e.currentTarget.src = HERO_IMG; } }} />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/35 to-transparent" />
          <div className="absolute inset-0 bg-black/30" />
        </motion.div>

        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-10 pb-20 md:pb-28 pt-32">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="font-mono text-xs md:text-sm tracking-[0.3em] uppercase text-gold mb-8"
          >
            Gestionnaire de paye · Tiers déclarant · Depuis 2017
          </motion.p>
          <h1 className="font-serif text-4xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tight max-w-5xl">
            <RevealLine delay={0.1}>La paye des artistes</RevealLine>
            <RevealLine delay={0.25}>
              <span className="italic text-gold">& des techniciens</span>
            </RevealLine>
            <RevealLine delay={0.4}>
              <span className="hero-vague inline-block cursor-default" data-testid="hero-vague">
                {"du spectacle vivant.".split("").map((ch, k) => (
                  <span key={k} className="vague-lettre" style={{ animationDelay: `${k * 45}ms` }}>
                    {ch === " " ? " " : ch}
                  </span>
                ))}
              </span>
            </RevealLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6"
          >
            <p className="text-[#A0B2CE] max-w-md text-base leading-relaxed">
              Nous prenons en charge la gestion de vos payes de A à Z, en toute conformité et confidentialité.
            </p>
            <a
              href="#contact"
              data-testid="hero-cta"
              className="cta-souffle group inline-flex items-center gap-3 bg-gold text-ink px-8 py-4 rounded-full font-mono text-xs uppercase tracking-[0.15em] hover:bg-[#B5952F] transition-colors duration-300 w-fit"
            >
              Demander des informations
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* SPECTACLES EN COURS */}
      {spectacles.length > 0 && (
        <section data-testid="spectacles-section" className="py-20 md:py-28 border-t border-white/10">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <p className="font-mono text-sm md:text-base tracking-[0.3em] uppercase text-gold mb-12 text-center">
              / Votre actualité, vos spectacles
            </p>
            <ul className="max-w-3xl mx-auto space-y-6">
              {spectacles.map((s, i) => {
                const poster = s.img ? (
                  <img
                    src={`${API}/public/spectacles/image/${s.img}`}
                    alt={`Affiche — ${s.title}`}
                    loading="lazy"
                    data-testid={`spectacle-img-${i}`}
                    className="w-12 h-16 md:w-14 md:h-20 object-cover rounded-sm border border-gold/40 shrink-0"
                  />
                ) : (
                  <img
                    src="/pastille-30.png"
                    alt=""
                    aria-hidden="true"
                    data-testid={`spectacle-pastille-${i}`}
                    className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-gold/50 object-cover shrink-0"
                  />
                );
                return (
                  <li key={i} className="border-b border-white/10 pb-6">
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`spectacle-link-${i}`}
                        className="group flex items-center gap-5 font-serif text-xl md:text-2xl hover:text-gold transition-colors">
                        {poster}
                        <span className="flex-1">{s.title}</span>
                        <ArrowUpRight className="w-5 h-5 text-gold shrink-0 group-hover:rotate-45 transition-transform duration-300" />
                      </a>
                    ) : (
                      <div className="flex items-center gap-5 font-serif text-xl md:text-2xl" data-testid={`spectacle-item-${i}`}>
                        {poster}
                        <span className="flex-1">{s.title}</span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            {spectaclesFresh && (
              <p
                data-testid="spectacles-fresh-badge"
                className="mt-10 flex items-center justify-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-gold/80"
              >
                <Sparkles className="w-4 h-4" />
                Mis à jour ce mois-ci
              </p>
            )}
          </div>
        </section>
      )}

      {/* LOGO PLAQUE */}
      <section data-testid="logo-section" className="relative py-24 md:py-32 border-t border-white/10 flex flex-col items-center">
        <motion.div
          {...fadeUp}
          className="bg-white rounded-2xl px-6 py-5 md:px-8 md:py-6 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.25)]"
        >
          <img src={LOGO} alt="Logo Le 30 du mois" className="logo-souffle logo-souffle-centre h-24 md:h-32 w-auto object-contain" data-testid="brand-logo-img" />
        </motion.div>
        <motion.p {...fadeUp} className="font-mono text-xs tracking-[0.3em] uppercase text-[#A0B2CE] mt-10 text-center">
          Le rendez-vous de votre paye, chaque 30 du mois.
        </motion.p>
      </section>

      {/* MANIFESTO */}
      <section id="metier" data-testid="manifesto-section" className="relative py-24 md:py-40 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.h2 {...fadeUp} className="font-mono text-sm md:text-base tracking-[0.3em] uppercase text-gold mb-20">
            / Le manifeste
          </motion.h2>
          <div className="space-y-px">
            {CHAPTERS.map((c) => (
              <motion.div
                key={c.n}
                {...fadeUp}
                className="group grid md:grid-cols-12 gap-6 md:gap-10 py-12 border-t border-white/10 hover:bg-white/[0.02] transition-colors duration-500"
              >
                <div className="md:col-span-2 font-mono text-5xl md:text-6xl text-white/15 group-hover:text-gold/60 transition-colors duration-500">
                  {c.n}
                </div>
                <h3 className="md:col-span-4 font-serif text-3xl md:text-4xl leading-tight">{c.title}</h3>
                <div className="md:col-span-6 text-[#A0B2CE] text-lg leading-relaxed self-center">{c.body}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" data-testid="services-section" className="relative py-24 md:py-40 bg-[#0B2A52]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.div {...fadeUp} className="max-w-2xl mb-20">
            <p className="font-mono text-sm md:text-base tracking-[0.3em] uppercase text-gold mb-6">/ Nos prestations</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">
              La gestion de la paye, <span className="italic text-gold">de A à Z.</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: (i % 3) * 0.08 }}
                className="group p-10 border border-white/10 -ml-px -mt-px hover:bg-ink transition-colors duration-500"
                data-testid={`service-${i}`}
              >
                <s.icon className="w-7 h-7 text-gold mb-8" strokeWidth={1.2} />
                <h3 className="font-serif text-2xl mb-3">{s.title}</h3>
                <p className="text-[#A0B2CE] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE / REFERENCES */}
      <section id="references" data-testid="references-section" className="py-20 md:py-28 border-y border-white/10">
        <p className="font-mono text-sm md:text-base tracking-[0.3em] uppercase text-gold text-center mb-12">
          / Ils nous font confiance
        </p>
        <Marquee gradient gradientColor="#062042" gradientWidth={160} speed={65} autoFill>
          {clients.map((c) => {
            const idx = c.lastIndexOf(" (");
            const hasVille = idx > 0 && c.endsWith(")");
            const nom = hasVille ? c.slice(0, idx) : c;
            const ville = hasVille ? c.slice(idx) : "";
            return (
              <span key={c} className="font-serif text-2xl md:text-4xl text-white/40 mx-10 md:mx-16 whitespace-nowrap" data-testid={`marquee-${nom}`}>
                {nom}{ville && <span className="text-white/25 text-xl md:text-3xl"> {ville}</span>} <span className="text-gold/70">·</span>
              </span>
            );
          })}
        </Marquee>
      </section>

      {/* SITES DES COMPAGNIES CLIENTES — sélection de 5 (5 premières lignes du fichier SITES.xlsx) */}
      {sites.length > 0 && (
        <section data-testid="sites-section" className="py-20 md:py-24 border-b border-white/10">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
            <p className="font-mono text-sm md:text-base tracking-[0.3em] uppercase text-gold mb-12">
              / Une sélection de site de nos compagnies
            </p>
            <ul className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5">
              {sites.map((s, i) => (
                <li key={i}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`site-link-${i}`}
                    className="group inline-flex items-center gap-2 font-serif text-xl md:text-2xl hover:text-gold transition-colors">
                    {s.label}
                    <ArrowUpRight className="w-4 h-4 text-gold shrink-0 group-hover:rotate-45 transition-transform duration-300" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* DIAPORAMA SPECTACLES */}
      <Diaporama mode="scroll" heightClass="h-[30vh] md:h-[40vh]" />

      {/* CONTACT */}
      <section id="contact" data-testid="contact-section" className="relative py-24 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24">
          <motion.div {...fadeUp}>
            <p className="font-mono text-sm md:text-base tracking-[0.3em] uppercase text-gold mb-6">/ Contact</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-8">
              Parlons de <span className="italic text-gold">votre paye.</span>
            </h2>
            <p className="text-[#A0B2CE] text-lg leading-relaxed max-w-md">
              Une question, un devis, un accompagnement pour votre compagnie ? Remplissez le formulaire, nous
              revenons vers vous sous 24h ouvrées.
            </p>
            <div className="mt-14 space-y-4 font-mono text-sm text-[#A0B2CE]">
              <p className="flex items-center gap-3"><Lock className="w-4 h-4 text-gold" /> Vos données sont traitées dans le respect du RGPD.</p>
            </div>
          </motion.div>

          <motion.form {...fadeUp} onSubmit={submit} data-testid="contact-form" className="space-y-8">
            {[
              { k: "name", label: "Nom & prénom *", type: "text", required: true },
              { k: "structure", label: "Compagnie / structure", type: "text" },
              { k: "email", label: "Email *", type: "email", required: true },
              { k: "phone", label: "Téléphone", type: "tel" },
            ].map((f) => (
              <div key={f.k}>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">{f.label}</label>
                <input
                  type={f.type}
                  required={f.required}
                  value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  data-testid={`contact-${f.k}`}
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300"
                />
              </div>
            ))}
            <div>
              <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Votre message *</label>
              <textarea
                required
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
                className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg resize-none transition-colors duration-300"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="group inline-flex items-center gap-3 bg-gold text-ink px-8 py-4 rounded-full font-mono text-xs uppercase tracking-[0.15em] hover:bg-[#B5952F] transition-colors duration-300 disabled:opacity-50"
            >
              {sending ? "Envoi…" : "Envoyer ma demande"}
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
            </button>
          </motion.form>
        </div>
      </section>

      {/* ESPACE D'ÉCHANGE — bourse communautaire réservée aux clients (accès par email) */}
      <section id="bourse" data-testid="board-section" className="relative py-24 md:py-40 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div {...fadeUp}>
            <p className="font-mono text-sm md:text-base tracking-[0.3em] uppercase text-gold mb-6">/ Communauté</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-8">
              L'espace <span className="italic text-gold">d'échange.</span>
            </h2>
            <p className="text-[#A0B2CE] text-lg leading-relaxed max-w-md">
              Entre clients du 30 du mois, on s'entraide : une offre d'emploi, une structure qui cherche
              un·e collaborateur·rice, un service à proposer… Consultez et publiez les annonces de la communauté.
            </p>
            <p className="mt-8 flex items-center gap-3 font-mono text-sm text-[#A0B2CE]">
              <Lock className="w-4 h-4 text-gold" /> Réservé aux clients — votre email suffit, aucun mot de passe.
            </p>
          </motion.div>
          <motion.form {...fadeUp} onSubmit={boardEnter} className="space-y-8" data-testid="board-landing-form">
            <div>
              <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Votre email de client</label>
              <input
                type="email"
                required
                value={boardEmail}
                onChange={(e) => setBoardEmail(e.target.value)}
                data-testid="board-landing-email"
                className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300"
              />
            </div>
            {boardErr && <p className="text-sm text-[#E74C3C]" data-testid="board-landing-error">{boardErr}</p>}
            <button
              type="submit"
              disabled={boardBusy}
              data-testid="board-landing-submit"
              className="group inline-flex items-center gap-3 bg-gold text-ink px-8 py-4 rounded-full font-mono text-xs uppercase tracking-[0.15em] hover:bg-[#B5952F] transition-colors duration-300 disabled:opacity-50"
            >
              {boardBusy ? "Vérification…" : "Entrer dans l'espace"}
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
            </button>
          </motion.form>
        </div>
      </section>

      {/* PRODUCTEUR / ÉQUIPE */}
      <Producteur />

      {/* LIENS UTILES — SPECTACLE VIVANT */}
      {liens.length > 0 && (
        <section id="liens" data-testid="liens-section" className="relative py-24 md:py-40 bg-[#0B2A52]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <motion.div {...fadeUp} className="max-w-2xl mb-20">
              <p className="font-mono text-sm md:text-base tracking-[0.3em] uppercase text-gold mb-6">/ Liens utiles</p>
              <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">
                Le spectacle vivant : <span className="italic text-gold">{liens.length - 1} repères, <CuriositeWord text="1 curiosité." /></span>
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3">
              {liens.map((l, i) => {
                const isCurio = /baroque/i.test(l.label); // la « 1 curiosité » du titre
                return (
                <motion.a
                  key={i}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: (i % 3) * 0.08 }}
                  className={`group p-10 border border-white/10 -ml-px -mt-px hover:bg-ink transition-colors duration-500 flex items-center justify-between gap-6${isCurio ? " curiosite-tile" : ""}`}
                  data-testid={isCurio ? "lien-curiosite" : `lien-${i}`}
                >
                  <span className="font-serif text-2xl leading-snug group-hover:text-gold transition-colors">
                    {isCurio
                      ? l.label.split("").map((ch, k) => (
                          <span key={k} className="ballet-letter" style={{ animationDelay: `${k * 60}ms` }}>
                            {ch === " " ? " " : ch}
                          </span>
                        ))
                      : l.label}
                  </span>
                  <ArrowUpRight className="w-6 h-6 text-gold shrink-0 group-hover:rotate-45 transition-transform duration-300" />
                </motion.a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* POUR LE PLAISIR — PHOTOS PERSONNELLES */}
      <Diaporama
        endpoint="/public/plaisir"
        kicker="/ Pour le plaisir"
        heading="Rien à voir avec la paye…"
        testid="plaisir"
        heightClass="h-[28vh] md:h-[36vh]"
        widthClass="max-w-[900px]"
        caption="photos personnelles"
      />

      {/* MERCI — mot de la fin */}
      <div className="pb-20 md:pb-24 -mt-6 md:-mt-10 text-center" data-testid="merci-section">
        <p className={`font-serif italic text-3xl md:text-4xl text-gold inline-block${merciVit ? " merci-vit" : ""}`} data-testid="merci-text">Merci</p>
        <div className="mt-10 flex justify-center" data-testid="jsm-teaser-logo">
          <Link to="/budget" title="Ouvrir l'application web Je Suis Mon Budget">
            <img src="/images/jsm-logo-sombre.png" alt="Je Suis Mon Budget"
              className="h-20 md:h-24 w-auto rounded-2xl border border-white/10 shadow-[0_24px_70px_rgba(0,0,0,0.45)] hover:border-gold/40 transition-colors duration-300" />
          </Link>
        </div>
        <div className="mt-6">
          <Link to="/budget" data-testid="jsm-open-app"
            className="inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-3 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-gold hover:text-ink transition-colors duration-300">
            Ouvrir l'application web
          </Link>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-14" data-testid="footer">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row justify-between gap-8">
          <div>
            <p className="font-serif text-2xl">Le 30 <span className="text-gold italic">du mois</span></p>
            <p className="font-mono text-xs text-[#A0B2CE] mt-3 tracking-wide">
              Gestionnaire de paye & tiers déclarant · Spectacle vivant
            </p>
          </div>
          <div className="flex items-start gap-10">
            <Link to="/connexion" className="font-mono text-xs uppercase tracking-[0.15em] text-[#A0B2CE] hover:text-gold transition-colors">
              Espace client
            </Link>
            <Link to="/mentions-legales" data-testid="footer-legal" className="font-mono text-xs uppercase tracking-[0.15em] text-[#A0B2CE] hover:text-gold transition-colors">
              Mentions légales & confidentialité
            </Link>
            <span className="font-mono text-xs text-[#A0B2CE]">© {new Date().getFullYear()}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
